import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import {
  COMPARISON_TABLE_PROPS,
  DEFAULT_MIN_READABLE_SCALE,
  SUMMARY_REPORT_TABLE_PROPS,
  X_AXIS_LABEL,
  Y_AXIS_LABEL,
} from '@/summaryReport/summaryData.constants';
import { asFormulaItem, rehydrateSummaryReportState } from '@/summaryReport/summaryReportItem.utilities';
import { type PROPS_TO_DEHYDRATE } from '@/trendData/baseItem.constants';
import { pruneDehydratedItemProps } from '@/trendData/itemStore.utilities';
import { baseItemInitialState } from '@/trendData/itemStoreBase.constants';
import { createBaseItemActions, removeItemsOnDraft } from '@/trendData/itemStoreBase.utilities';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import { type ActionsFactory, type StoreInstance, createStore, createStoreInstance } from '@seeqdev/utils';
import type { ChartPoint, ExpandedPanelState, StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import type { BaseItemActions, BaseItemState } from '@/trendData/itemStoreBase.types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SummaryReportState extends BaseItemState {
  /** The statistics summaries currently open in the Summary Report. */
  items: StatisticsSummaryItem[];
  /** ID of the summary every other summary is compared against, or `null` when not in compare mode. */
  baselineId: string | null;
  /** Chart points the user has selected; each one is mirrored by a calendar cursor. */
  selectedPoints: ChartPoint[];
  /** Whether all summaries are drawn on a single set of overlaid charts. */
  isItemsOverlaid: boolean;
  showHistogram: boolean;
  showBoxPlot: boolean;
  showTable: boolean;
  showBellCurve: boolean;
  /** Load status of the summaries' data, driving the spinner and error views. */
  status: ITEM_DATA_STATUS;
  xAxisLabel: X_AXIS_LABEL;
  yAxisLabel: Y_AXIS_LABEL;
  showSpecificationLimits: boolean;
  showBoxplotPoints: boolean;
  /** Keys of the statistics rendered as rows of the summary table. */
  visibleStats: string[];
  showSignal: boolean;
  showTimeRange: boolean;
  showCondition: boolean;
  showControlCharts: boolean;
  showSigmaLevels: boolean;
  showControlLimits: boolean;
  showRunRules: boolean;
  showRunRuleViolations: boolean;
  showIssues: boolean;
  columnScaling: boolean;
  minReadableScale: number;
  expandedPanel: ExpandedPanelState | null;
}

type SummaryReportDehydratedItem = Pick<
  StatisticsSummaryItem,
  Extract<(typeof PROPS_TO_DEHYDRATE)[number], keyof StatisticsSummaryItem>
> & {
  selected?: boolean;
  autoDisabled?: boolean;
};

export type SummaryReportDehydratedState = Omit<SummaryReportState, keyof BaseItemState | 'status'> & {
  items: SummaryReportDehydratedItem[];
};

export interface SummaryReportActions extends BaseItemActions {
  /** Replaces the full summary list, tagging each entry as a formula item. */
  setItems(items: StatisticsSummaryItem[]): void;
  /** Sets (or clears, when passed `null`) the summary the others are compared against. */
  setBaseline(item: StatisticsSummaryItem | null): void;
  /** Appends the summary, or replaces it in place when it is already open. */
  addItem(item: StatisticsSummaryItem): void;
  /** Moves the summary to `index`, inserting it when it is not open yet. */
  insertItemAtIndex(item: StatisticsSummaryItem, index: number): void;
  updateItem(item: StatisticsSummaryItem): void;
  setStatus(status: ITEM_DATA_STATUS): void;
  setIsItemsOverlaid(isItemsOverlaid: boolean): void;
  setShowHistogram(showHistogram: boolean): void;
  setShowBoxPlot(showBoxPlot: boolean): void;
  setShowTable(showTable: boolean): void;
  setShowControlCharts(showControlCharts: boolean): void;
  /** Sets the visible table statistics; the list is sorted so the table row order stays stable. */
  setVisibleStats(visibleStats: string[]): void;
  setShowBellCurve(showBellCurve: boolean): void;
  setXAxisLabel(xAxisLabel: X_AXIS_LABEL): void;
  setYAxisLabel(yAxisLabel: Y_AXIS_LABEL): void;
  setShowSpecificationLimits(showSpecificationLimits: boolean): void;
  setShowSignal(showSignal: boolean): void;
  setShowTimeRange(showTimeRange: boolean): void;
  setShowCondition(showCondition: boolean): void;
  setShowBoxplotPoints(showBoxplotPoints: boolean): void;
  setShowSigmaLevels(showSigmaLevels: boolean): void;
  setShowControlLimits(showControlLimits: boolean): void;
  setShowRunRules(showRunRules: boolean): void;
  setShowRunRuleViolations(showRunRuleViolations: boolean): void;
  setShowIssues(showIssues: boolean): void;
  setSelectedPoints(selectedPoints: ChartPoint[]): void;
  setColumnScaling(columnScaling: boolean): void;
  setMinReadableScale(minReadableScale: number): void;
  setExpandedPanel(expandedPanel: ExpandedPanelState | null): void;
  /** Restores every display toggle to its default without touching the open summaries. */
  resetView(): void;
  reset(overrides?: Partial<SummaryReportState>): void;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

/** Display toggles restored by `resetView`, and the defaults the store starts with. */
const DEFAULT_VIEW_STATE = {
  showHistogram: true,
  showBoxPlot: true,
  showTable: true,
  showBellCurve: true,
  showControlCharts: true,
  showSignal: true,
  showTimeRange: true,
  showCondition: true,
  showRunRules: true,
  showRunRuleViolations: true,
  showControlLimits: true,
  showSpecificationLimits: true,
  showBoxplotPoints: true,
  showSigmaLevels: false,
  showIssues: true,
  columnScaling: true,
  minReadableScale: DEFAULT_MIN_READABLE_SCALE,
  xAxisLabel: X_AXIS_LABEL.UNIT,
  yAxisLabel: Y_AXIS_LABEL.COUNT,
};

export const initialState: SummaryReportState = Object.freeze({
  ...baseItemInitialState,
  ...DEFAULT_VIEW_STATE,
  items: [],
  baselineId: null,
  selectedPoints: [],
  isItemsOverlaid: false,
  status: ITEM_DATA_STATUS.INITIALIZING,
  visibleStats: [...SUMMARY_REPORT_TABLE_PROPS, ...COMPARISON_TABLE_PROPS].map((prop) => prop.key).sort(),
  expandedPanel: null,
});

// ---------------------------------------------------------------------------
// Dehydrate / rehydrate
// ---------------------------------------------------------------------------

export const dehydrateSummaryReportState = (state: SummaryReportState): SummaryReportDehydratedState => ({
  items: state.items.map(
    (item) => pruneDehydratedItemProps(item as unknown as Record<string, unknown>) as SummaryReportDehydratedItem,
  ),
  baselineId: state.baselineId,
  selectedPoints: state.selectedPoints,
  isItemsOverlaid: state.isItemsOverlaid,
  showHistogram: state.showHistogram,
  showBoxPlot: state.showBoxPlot,
  showTable: state.showTable,
  showBellCurve: state.showBellCurve,
  showControlCharts: state.showControlCharts,
  visibleStats: state.visibleStats,
  xAxisLabel: state.xAxisLabel,
  yAxisLabel: state.yAxisLabel,
  showSpecificationLimits: state.showSpecificationLimits,
  showBoxplotPoints: state.showBoxplotPoints,
  showSigmaLevels: state.showSigmaLevels,
  showControlLimits: state.showControlLimits,
  showSignal: state.showSignal,
  showTimeRange: state.showTimeRange,
  showCondition: state.showCondition,
  showRunRules: state.showRunRules,
  showRunRuleViolations: state.showRunRuleViolations,
  showIssues: state.showIssues,
  columnScaling: state.columnScaling,
  minReadableScale: state.minReadableScale,
  expandedPanel: state.expandedPanel,
});

// ---------------------------------------------------------------------------
// Actions factory
// ---------------------------------------------------------------------------

const STORE_NAME = 'SummaryReport';

const clampOverlayToItemCount = (draft: Pick<SummaryReportState, 'items' | 'isItemsOverlaid'>): void => {
  if (draft.items.length <= 1) {
    draft.isItemsOverlaid = false;
  }
};

const summaryReportActionsFactory: ActionsFactory<SummaryReportState, SummaryReportActions> = (
  set,
  _get,
  resetState,
) => ({
  ...createBaseItemActions<SummaryReportState>(set),

  removeItems: (payload) =>
    set((draft) => {
      removeItemsOnDraft(draft, payload);
      clampOverlayToItemCount(draft);
    }),

  setItems: (items) =>
    set((draft) => {
      draft.items = (items ?? []).map(asFormulaItem);
      clampOverlayToItemCount(draft);
    }),

  setBaseline: (item) =>
    set((draft) => {
      draft.baselineId = item?.id ?? null;
      draft.expandedPanel = null;
    }),

  addItem: (item) =>
    set((draft) => {
      const index = draft.items.findIndex((existing) => existing.id === item.id);
      if (index === -1) {
        draft.items.push(asFormulaItem(item));
      } else {
        draft.items[index] = asFormulaItem(item);
      }
    }),

  insertItemAtIndex: (item, index) =>
    set((draft) => {
      const without = draft.items.filter((existing) => existing.id !== item.id);
      const clamped = Math.max(0, Math.min(index, without.length));
      without.splice(clamped, 0, asFormulaItem(item));
      draft.items = without;
    }),

  updateItem: (item) =>
    set((draft) => {
      const index = draft.items.findIndex((existing) => existing.id === item.id);
      if (index !== -1) {
        draft.items[index] = asFormulaItem(item);
      }
    }),

  setStatus: (status) =>
    set((draft) => {
      draft.status = status;
    }),

  setIsItemsOverlaid: (isItemsOverlaid) =>
    set((draft) => {
      draft.isItemsOverlaid = isItemsOverlaid;
      draft.expandedPanel = null;
    }),

  setShowHistogram: (showHistogram) =>
    set((draft) => {
      draft.showHistogram = showHistogram;
      if (!showHistogram && draft.expandedPanel?.panel === 'histogram') {
        draft.expandedPanel = null;
      }
    }),

  setShowBoxPlot: (showBoxPlot) =>
    set((draft) => {
      draft.showBoxPlot = showBoxPlot;
      if (!showBoxPlot && draft.expandedPanel?.panel === 'boxPlot') {
        draft.expandedPanel = null;
      }
    }),

  setShowTable: (showTable) =>
    set((draft) => {
      draft.showTable = showTable;
      if (!showTable && draft.expandedPanel?.panel === 'table') {
        draft.expandedPanel = null;
      }
    }),

  setShowControlCharts: (showControlCharts) =>
    set((draft) => {
      draft.showControlCharts = showControlCharts;
      if (!showControlCharts && draft.expandedPanel?.panel === 'controlCharts') {
        draft.expandedPanel = null;
      }
    }),

  setVisibleStats: (visibleStats) =>
    set((draft) => {
      draft.visibleStats = [...visibleStats].sort();
    }),

  setShowBellCurve: (showBellCurve) =>
    set((draft) => {
      draft.showBellCurve = showBellCurve;
    }),

  setXAxisLabel: (xAxisLabel) =>
    set((draft) => {
      draft.xAxisLabel = xAxisLabel;
    }),

  setYAxisLabel: (yAxisLabel) =>
    set((draft) => {
      draft.yAxisLabel = yAxisLabel;
    }),

  setShowSpecificationLimits: (showSpecificationLimits) =>
    set((draft) => {
      draft.showSpecificationLimits = showSpecificationLimits;
    }),

  setShowSignal: (showSignal) =>
    set((draft) => {
      draft.showSignal = showSignal;
    }),

  setShowTimeRange: (showTimeRange) =>
    set((draft) => {
      draft.showTimeRange = showTimeRange;
    }),

  setShowCondition: (showCondition) =>
    set((draft) => {
      draft.showCondition = showCondition;
    }),

  setShowBoxplotPoints: (showBoxplotPoints) =>
    set((draft) => {
      draft.showBoxplotPoints = showBoxplotPoints;
    }),

  setShowSigmaLevels: (showSigmaLevels) =>
    set((draft) => {
      draft.showSigmaLevels = showSigmaLevels;
    }),

  setShowControlLimits: (showControlLimits) =>
    set((draft) => {
      draft.showControlLimits = showControlLimits;
    }),

  setShowRunRules: (showRunRules) =>
    set((draft) => {
      draft.showRunRules = showRunRules;
    }),

  setShowRunRuleViolations: (showRunRuleViolations) =>
    set((draft) => {
      draft.showRunRuleViolations = showRunRuleViolations;
    }),

  setShowIssues: (showIssues) =>
    set((draft) => {
      draft.showIssues = showIssues;
    }),

  setSelectedPoints: (selectedPoints) =>
    set((draft) => {
      draft.selectedPoints = selectedPoints;
    }),

  setColumnScaling: (columnScaling) =>
    set((draft) => {
      draft.columnScaling = columnScaling;
    }),

  setMinReadableScale: (minReadableScale) =>
    set((draft) => {
      draft.minReadableScale = minReadableScale;
    }),

  setExpandedPanel: (expandedPanel) =>
    set((draft) => {
      draft.expandedPanel = expandedPanel;
    }),

  resetView: () =>
    set((draft) => {
      Object.assign(draft, DEFAULT_VIEW_STATE);
    }),

  reset: resetState,
});

const [SummaryReportStoreProvider, useSummaryReportStoreInstance, useSummaryReportSelector] = createStore(
  STORE_NAME,
  initialState,
  summaryReportActionsFactory,
);

export { SummaryReportStoreProvider };

export const useSummaryReportItems = () => useSummaryReportSelector((state) => state.items);
export const useSummaryReportBaselineId = () => useSummaryReportSelector((state) => state.baselineId);
export const useSummaryReportSelectedPoints = () => useSummaryReportSelector((state) => state.selectedPoints);
export const useSummaryReportIsItemsOverlaid = () => useSummaryReportSelector((state) => state.isItemsOverlaid);
export const useSummaryReportShowHistogram = () => useSummaryReportSelector((state) => state.showHistogram);
export const useSummaryReportShowBoxPlot = () => useSummaryReportSelector((state) => state.showBoxPlot);
export const useSummaryReportShowTable = () => useSummaryReportSelector((state) => state.showTable);
export const useSummaryReportShowBellCurve = () => useSummaryReportSelector((state) => state.showBellCurve);
export const useSummaryReportStatus = () => useSummaryReportSelector((state) => state.status);
export const useSummaryReportXAxisLabel = () => useSummaryReportSelector((state) => state.xAxisLabel);
export const useSummaryReportYAxisLabel = () => useSummaryReportSelector((state) => state.yAxisLabel);
export const useSummaryReportShowSpecificationLimits = () =>
  useSummaryReportSelector((state) => state.showSpecificationLimits);
export const useSummaryReportShowBoxplotPoints = () => useSummaryReportSelector((state) => state.showBoxplotPoints);
export const useSummaryReportVisibleStats = () => useSummaryReportSelector((state) => state.visibleStats);
export const useSummaryReportShowSignal = () => useSummaryReportSelector((state) => state.showSignal);
export const useSummaryReportShowTimeRange = () => useSummaryReportSelector((state) => state.showTimeRange);
export const useSummaryReportShowCondition = () => useSummaryReportSelector((state) => state.showCondition);
export const useSummaryReportShowControlCharts = () => useSummaryReportSelector((state) => state.showControlCharts);
export const useSummaryReportShowSigmaLevels = () => useSummaryReportSelector((state) => state.showSigmaLevels);
export const useSummaryReportShowControlLimits = () => useSummaryReportSelector((state) => state.showControlLimits);
export const useSummaryReportShowRunRules = () => useSummaryReportSelector((state) => state.showRunRules);
export const useSummaryReportShowRunRuleViolations = () =>
  useSummaryReportSelector((state) => state.showRunRuleViolations);
export const useSummaryReportShowIssues = () => useSummaryReportSelector((state) => state.showIssues);
export const useSummaryReportColumnScaling = () => useSummaryReportSelector((state) => state.columnScaling);
export const useSummaryReportMinReadableScale = () => useSummaryReportSelector((state) => state.minReadableScale);
export const useSummaryReportExpandedPanel = () => useSummaryReportSelector((state) => state.expandedPanel);

/** The baseline summary itself, or `null` when nothing is selected or the baseline was closed. */
const selectSummaryReportBaseline = createSelector(
  (state: SummaryReportState) => state.items,
  (state: SummaryReportState) => state.baselineId,
  (items, baselineId) => (baselineId ? (items.find((item) => item.id === baselineId) ?? null) : null),
);
export const useSummaryReportBaseline = () => useSummaryReportSelector(selectSummaryReportBaseline);

export const useSummaryReportActions = () => useSummaryReportStoreInstance().actions;

export const usePersistSummaryReportStore = () => {
  const register = useRegisterPersistedStore();
  const store = useSummaryReportStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqSummaryReportStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateSummaryReportState,
      rehydrate: (dehydratedState: Partial<SummaryReportDehydratedState>) =>
        rehydrateSummaryReportState(dehydratedState, (partial) => store.setState(partial)),
    });
  }, [register, store]);
};

type LegacySummaryReportStore = StoreInstance<SummaryReportState, SummaryReportActions>;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider initialSummaryReportData={...}>` with UI-driven assertions instead.
 */
export function createTestSummaryReportStore(overrides?: Partial<SummaryReportState>): LegacySummaryReportStore {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestSummaryReportStore must only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, summaryReportActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` (e.g. `findSummaryReportItem(id)`) and
 * mutate via `store.actions.*`. New code should use the hooks above and the Provider.
 */
export function legacyCreateSummaryReportStore(
  initialStateOverrides?: Partial<SummaryReportState>,
): LegacySummaryReportStore {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    summaryReportActionsFactory,
    { storeName: STORE_NAME },
  );
}
